import jsPDF from 'jspdf';
import { isSqmMode, sqmLineDisplay } from './signageSqmUtils';

export const generateSignagePDF = ({ form, calcResult, branches, logoBase64 }) => {
  if (!calcResult) return;
  const pdf = new jsPDF();
  const pw = 210; const m = 15; const cw = pw - 2 * m; let y = 15;

  const selectedBranch = branches.find(b => b.id === form.branch);

  // Header — matches PO style with salesperson contact + logo
  const spPhone = form.salesman_phone || '';
  const contactLine = `www.prismoline.com | sales@prismoline.com${spPhone ? ' | +91 ' + spPhone : ''}`;
  pdf.setFillColor(36, 84, 156); pdf.rect(0, 0, pw, 38, 'F');
  pdf.setTextColor(255); pdf.setFontSize(18); pdf.setFont(undefined, 'bold');
  pdf.text('SHARDA INFRASOLUTIONS PVT. LTD.', m, 14);
  pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
  pdf.text('Road Safety & Infrastructure Solutions', m, 22);
  pdf.setFontSize(7);
  pdf.text('CIN: U24100JH2010PTC014546 | PAN: AAPCS1562H', m, 28);
  pdf.text(contactLine, m, 34);

  if (logoBase64) {
    try { pdf.addImage(logoBase64, 'PNG', pw - m - 40, 4, 38, 28); } catch (e) { /* logo failed silently */ }
  }

  y = 46;
  pdf.setTextColor(36, 84, 156); pdf.setFontSize(14); pdf.setFont(undefined, 'bold');
  pdf.text('SIGNAGE QUOTATION', pw / 2, y, { align: 'center' }); y += 4;
  pdf.setDrawColor(36, 84, 156); pdf.setLineWidth(0.5); pdf.line(m, y, pw - m, y); y += 8;

  // Info section — Ref, Project, Date, Valid, Work, Branch, Salesman
  pdf.setTextColor(0); pdf.setFontSize(8);
  const leftX = m; const rightX = pw / 2 + 5;
  const infoStartY = y;

  if (form.quotation_number || form.project_name) {
    pdf.setFont(undefined, 'bold'); pdf.text('Ref:', leftX, y); pdf.setFont(undefined, 'normal');
    pdf.text(form.quotation_number || '', leftX + 22, y); y += 5;
  }
  if (form.project_name) { pdf.setFont(undefined, 'bold'); pdf.text('Project:', leftX, y); pdf.setFont(undefined, 'normal'); pdf.text(form.project_name, leftX + 22, y); y += 5; }
  if (form.stretch_no) { pdf.setFont(undefined, 'bold'); pdf.text('Stretch:', leftX, y); pdf.setFont(undefined, 'normal'); pdf.text(form.stretch_no, leftX + 22, y); y += 5; }
  pdf.setFont(undefined, 'bold'); pdf.text('Salesman:', leftX, y); pdf.setFont(undefined, 'normal'); pdf.text(form.salesman_name || '-', leftX + 22, y);

  let ry = infoStartY;
  pdf.setFont(undefined, 'bold'); pdf.text('Date:', rightX, ry); pdf.setFont(undefined, 'normal'); pdf.text(new Date().toISOString().slice(0, 10), rightX + 22, ry); ry += 5;
  pdf.setFont(undefined, 'bold'); pdf.text('Valid Until:', rightX, ry); pdf.setFont(undefined, 'normal'); pdf.text(form.valid_until || '-', rightX + 22, ry); ry += 5;
  // Compute unique work types across items (for mixed vs uniform PDF rendering)
  const itemWorkTypes = (form.items || []).map(i => i.work_type || form.work_type || '').filter(Boolean);
  const uniqueWorkTypes = Array.from(new Set(itemWorkTypes));
  const isMixedWorkTypes = uniqueWorkTypes.length > 1;
  const headerWorkType = isMixedWorkTypes ? '' : (uniqueWorkTypes[0] || form.work_type || '');
  if (form.work_for) {
    pdf.setFont(undefined, 'bold'); pdf.text('Work:', rightX, ry); pdf.setFont(undefined, 'normal');
    pdf.text(headerWorkType ? `${form.work_for} - ${headerWorkType}` : form.work_for, rightX + 22, ry); ry += 5;
  }

  y = Math.max(y, ry) + 8;

  // 3-Box Address Section (like PO): Supplier | Bill To | Ship To
  const hasShipTo = form.ship_to_name || form.ship_to_address;
  const numBoxes = hasShipTo ? 3 : 2;
  const boxWidth = numBoxes === 3 ? (cw - 6) / 3 : (cw - 3) / 2;
  const gap = 3;

  // Build address strings
  const branchAddr = selectedBranch ? [
    selectedBranch.address, selectedBranch.city, selectedBranch.state,
    selectedBranch.pincode ? `- ${selectedBranch.pincode}` : ''
  ].filter(Boolean).join(', ') : '';
  const branchGstin = selectedBranch?.gstin || '';

  const custAddr = [form.customer_address, form.customer_city, form.customer_state].filter(Boolean).join(', ');
  const custGstin = form.customer_gstin || '';

  const shipAddr = hasShipTo ? [form.ship_to_address, form.ship_to_city, form.ship_to_state].filter(Boolean).join(', ') : '';

  // Calculate dynamic box height
  const branchAddrLines = branchAddr ? pdf.splitTextToSize(branchAddr, boxWidth - 6) : [];
  const custAddrLines = custAddr ? pdf.splitTextToSize(custAddr, boxWidth - 6) : [];
  const shipAddrLines = shipAddr ? pdf.splitTextToSize(shipAddr, boxWidth - 6) : [];
  pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
  const custNameLines = pdf.splitTextToSize(form.customer_name || '-', boxWidth - 6);
  const shipNameLines = hasShipTo ? pdf.splitTextToSize(form.ship_to_name || '', boxWidth - 6) : [];
  pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
  const nameExtraLines = Math.max(custNameLines.length, shipNameLines.length, 1) - 1;
  const maxAddrLines = Math.max(branchAddrLines.length, custAddrLines.length, shipAddrLines.length);
  const boxHeight = Math.max(28, 18 + maxAddrLines * 3.5 + 6 + nameExtraLines * 4);

  // Draw boxes
  const box1X = m;
  const box2X = m + boxWidth + gap;
  const box3X = m + 2 * (boxWidth + gap);

  // Box 1: Supplier (Branch/Company)
  pdf.setFillColor(245, 247, 250); pdf.rect(box1X, y - 3, boxWidth, boxHeight, 'F');
  pdf.setTextColor(36, 84, 156); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
  pdf.text('SUPPLIER', box1X + 3, y + 2);
  pdf.setTextColor(0); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
  pdf.text('Sharda Infrasolutions Pvt Ltd', box1X + 3, y + 8);
  pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
  if (selectedBranch?.name) { pdf.text(selectedBranch.name, box1X + 3, y + 13); }
  let bAddrY = y + 17;
  branchAddrLines.forEach((line, i) => { pdf.text(line, box1X + 3, bAddrY + i * 3.5); });
  bAddrY += branchAddrLines.length * 3.5;
  if (branchGstin) { pdf.text(`GSTIN: ${branchGstin}`, box1X + 3, bAddrY); }

  // Box 2: Bill To (Customer)
  pdf.setFillColor(245, 247, 250); pdf.rect(box2X, y - 3, boxWidth, boxHeight, 'F');
  pdf.setTextColor(36, 84, 156); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
  pdf.text('BILL TO', box2X + 3, y + 2);
  pdf.setTextColor(0); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
  custNameLines.forEach((line, i) => pdf.text(line, box2X + 3, y + 8 + i * 4));
  pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
  let cAddrY = y + 13 + (custNameLines.length - 1) * 4;
  custAddrLines.forEach((line, i) => { pdf.text(line, box2X + 3, cAddrY + i * 3.5); });
  cAddrY += custAddrLines.length * 3.5;
  if (custGstin) { pdf.text(`GSTIN: ${custGstin}`, box2X + 3, cAddrY); }

  // Box 3: Ship To (optional)
  if (hasShipTo) {
    pdf.setFillColor(245, 247, 250); pdf.rect(box3X, y - 3, boxWidth, boxHeight, 'F');
    pdf.setTextColor(36, 84, 156); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
    pdf.text('SHIP TO', box3X + 3, y + 2);
    pdf.setTextColor(0); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
    shipNameLines.forEach((line, i) => pdf.text(line, box3X + 3, y + 8 + i * 4));
    pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
    let sAddrY = y + 13 + (shipNameLines.length - 1) * 4;
    shipAddrLines.forEach((line, i) => { pdf.text(line, box3X + 3, sAddrY + i * 3.5); });
  }

  y += boxHeight + 6;

  // Items Table — Columns mirror OC Signage PDF: # | Description | Shape | Dimensions | Qty | Rate/Board | Amount
  const colX = { num: m + 2, desc: m + 8, shape: m + 78, dims: m + 98, qty: m + 120, rate: m + 137, amount: m + 158 };
  const descColWidth = 68;

  pdf.setFillColor(36, 84, 156); pdf.rect(m, y, cw, 7, 'F');
  pdf.setTextColor(255); pdf.setFontSize(7.5); pdf.setFont(undefined, 'bold');
  ['#', 'Description', 'Shape', 'Dimensions', 'Qty', 'Rate', 'Amount'].forEach((h, i) => {
    pdf.text(h, [colX.num, colX.desc, colX.shape, colX.dims, colX.qty, colX.rate, colX.amount][i], y + 5);
  });
  y += 7;

  pdf.setTextColor(0); pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
  const Q_LINE_H = (pdf.getLineHeight ? pdf.getLineHeight() : 7 * 1.15) / pdf.internal.scaleFactor;
  const Q_PAD_TOP = 3.5;
  const Q_PAD_BOT = 3.5;
  const SPEC_LINE_H = (6.5 * 1.15) / pdf.internal.scaleFactor;

  const marginPct = Number(form.margin_percent) || 0;
  const overheadPct = Number(form.overhead_percent) || 0;
  const pdfMultiplier = 1 + (marginPct / 100) + (overheadPct / 100);

  calcResult.items.forEach((item, idx) => {
    const formItem = form.items[idx] || {};
    const isPcs = formItem.pricing_mode === 'per_pcs';
    const baseRate = isPcs ? (Number(formItem.custom_per_pcs_rate) || item.final_per_board) : item.final_per_board;
    const displayRate = Math.round(baseRate * pdfMultiplier);
    const lineGross = displayRate * item.qty;
    const dType = formItem.disc_type || 'amount';
    const dVal = Number(formItem.disc_value) || 0;
    let lineDisc = dVal > 0 ? (dType === 'percent' ? Math.round(lineGross * dVal / 100) : Math.round(dVal)) : 0;
    lineDisc = Math.min(lineDisc, lineGross);
    const displayTotal = lineGross - lineDisc;

    // Description column: board_description + [work_type] tag if mixed + spec description (smaller font) under it
    const rowWorkType = formItem.work_type || form.work_type || '';
    const wtTag = (isMixedWorkTypes && rowWorkType) ? `[${rowWorkType}]` : '';
    const boardDesc = formItem.description || '-';
    const mainLines = pdf.splitTextToSize(wtTag ? `${boardDesc}\n${wtTag}` : boardDesc, descColWidth);

    pdf.setFontSize(6.5);
    const specDescRaw = formItem.specific_description || '';
    const discNote = lineDisc > 0 ? (dType === 'percent' ? `Discount: ${dVal}% (-INR ${lineDisc.toLocaleString('en-IN')})` : `Discount: -INR ${lineDisc.toLocaleString('en-IN')}`) : '';
    const specDesc = [specDescRaw ? `Description: ${specDescRaw}` : '', discNote].filter(Boolean).join('  •  ');
    const specLines = specDesc ? pdf.splitTextToSize(specDesc, descColWidth) : [];
    pdf.setFontSize(7);

    const textH = mainLines.length * Q_LINE_H + specLines.length * SPEC_LINE_H + (specLines.length > 0 ? 1 : 0);
    const rowH = Math.max(8, textH + Q_PAD_TOP + Q_PAD_BOT);

    if (y + rowH > 272) { pdf.addPage(); y = 15; }
    if (idx % 2 === 1) { pdf.setFillColor(248, 249, 252); pdf.rect(m, y, cw, rowH, 'F'); }

    pdf.setTextColor(0);
    const baseY = y + Q_PAD_TOP;
    pdf.setFontSize(7);
    pdf.text(`${idx + 1}`, colX.num, baseY);
    pdf.text(mainLines, colX.desc, baseY);
    pdf.text(`${item.shape || '-'}`, colX.shape, baseY);
    pdf.text(`${item.dimensions || '-'}`, colX.dims, baseY);
    // "Per Sq.m" mode: show total sqm as Qty and per-sqm rate. Amount unchanged.
    const _sqmItem = { ...item, ...formItem, pricing_mode: formItem.pricing_mode };
    if (isSqmMode(_sqmItem)) {
      const { totalSqm, ratePerSqm } = sqmLineDisplay(_sqmItem, item.qty, displayTotal);
      pdf.text(`${totalSqm} SQM`, colX.qty, baseY);
      pdf.text(`INR ${ratePerSqm.toLocaleString('en-IN')}`, colX.rate, baseY);
    } else {
      pdf.text(`${item.qty || 0}`, colX.qty, baseY);
      pdf.text(`INR ${Math.round(displayRate).toLocaleString('en-IN')}`, colX.rate, baseY);
    }
    pdf.text(`INR ${Math.round(displayTotal).toLocaleString('en-IN')}`, colX.amount, baseY);

    if (specLines.length > 0) {
      pdf.setFontSize(6.5); pdf.setTextColor(100);
      const specStartY = baseY + (mainLines.length * Q_LINE_H) + 1;
      pdf.text(specLines, colX.desc, specStartY);
      pdf.setFontSize(7); pdf.setTextColor(0);
    }

    y += rowH;
    pdf.setDrawColor(220); pdf.line(m, y, m + cw, y);
  });

  // Totals (margin/overhead already baked into item rates)
  y += 3; pdf.setDrawColor(200); pdf.setLineWidth(0.3); pdf.line(m, y, pw - m, y); y += 7;
  const totLabelX = m + 100; const totValX = pw - m - 2;

  let itemsGrossPdf = 0, itemDiscTotalPdf = 0;
  calcResult.items.forEach((item, idx) => {
    const formItem = form.items[idx] || {};
    const isPcs = formItem.pricing_mode === 'per_pcs';
    const baseRate = isPcs ? (Number(formItem.custom_per_pcs_rate) || item.final_per_board) : item.final_per_board;
    const g = Math.round(baseRate * pdfMultiplier) * item.qty;
    const dType = formItem.disc_type || 'amount';
    const dVal = Number(formItem.disc_value) || 0;
    let d = dVal > 0 ? (dType === 'percent' ? Math.round(g * dVal / 100) : Math.round(dVal)) : 0;
    d = Math.min(d, g);
    itemsGrossPdf += g; itemDiscTotalPdf += d;
  });
  const transportAmt = parseFloat(form.transportation_amount) || 0;
  const netItemsPdf = itemsGrossPdf - itemDiscTotalPdf;
  const netTotalPdf = netItemsPdf + transportAmt;
  const oDType = form.discount_type || 'amount';
  const oDVal = Number(form.discount_value) || 0;
  let overallDiscPdf = oDVal > 0 ? (oDType === 'percent' ? Math.round(netTotalPdf * oDVal / 100) : Math.round(oDVal)) : 0;
  overallDiscPdf = Math.min(overallDiscPdf, netTotalPdf);
  const subtotalPdf = netTotalPdf - overallDiscPdf; // taxable
  const gstAmtPdf = Math.round(subtotalPdf * form.gst_rate / 100);
  const grandTotalPdf = subtotalPdf + gstAmtPdf;

  pdf.setFontSize(8);
  pdf.setFont(undefined, 'normal'); pdf.text('Items Total:', totLabelX, y); pdf.setFont(undefined, 'bold'); pdf.text(`INR ${Math.round(itemsGrossPdf).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 6;
  if (itemDiscTotalPdf > 0) { pdf.setFont(undefined, 'normal'); pdf.text('Item Discounts:', totLabelX, y); pdf.text(`- INR ${Math.round(itemDiscTotalPdf).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 6; }
  if (transportAmt > 0) { pdf.setFont(undefined, 'normal'); pdf.text('Transport:', totLabelX, y); pdf.text(`INR ${Math.round(transportAmt).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 6; }
  pdf.setFont(undefined, 'normal'); pdf.text('Net Total:', totLabelX, y); pdf.setFont(undefined, 'bold'); pdf.text(`INR ${Math.round(netTotalPdf).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 6;
  if (overallDiscPdf > 0) {
    pdf.setFont(undefined, 'normal'); pdf.text(`Discount${oDType === 'percent' ? ' (' + oDVal + '%)' : ''}:`, totLabelX, y); pdf.text(`- INR ${Math.round(overallDiscPdf).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 6;
    pdf.text('Taxable Value:', totLabelX, y); pdf.setFont(undefined, 'bold'); pdf.text(`INR ${Math.round(subtotalPdf).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 6;
  }
  pdf.setFont(undefined, 'normal'); pdf.text(`GST (${form.gst_rate}%):`, totLabelX, y); pdf.text(`INR ${gstAmtPdf.toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 7;
  pdf.setFillColor(36, 84, 156); pdf.rect(totLabelX - 3, y - 5, pw - m - totLabelX + 5, 9, 'F');
  pdf.setTextColor(255); pdf.setFont(undefined, 'bold'); pdf.setFontSize(9);
  pdf.text('Total:', totLabelX, y); pdf.text(`INR ${grandTotalPdf.toLocaleString('en-IN')}`, totValX, y, { align: 'right' });

  // Terms
  y += 16;
  if (y > 250) { pdf.addPage(); y = 15; }
  pdf.setTextColor(36, 84, 156); pdf.setFontSize(9); pdf.setFont(undefined, 'bold');
  pdf.text('Terms & Conditions', m, y); y += 2;
  pdf.setDrawColor(36, 84, 156); pdf.setLineWidth(0.3); pdf.line(m, y, m + 35, y); y += 5;

  pdf.setTextColor(60); pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
  Object.entries(form.terms).forEach(([key, val]) => {
    if (!val) return;
    if (y > 277) { pdf.addPage(); y = 15; }
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    pdf.setFont(undefined, 'bold'); pdf.text(`${label}:`, m, y);
    pdf.setFont(undefined, 'normal');
    const maxW = cw - 35;
    const lines = pdf.splitTextToSize(String(val), maxW);
    lines.forEach((line, li) => { pdf.text(line, m + 33, y + (li * 4)); });
    y += Math.max(5, lines.length * 4 + 1);
  });

  // Footer
  const footerY = 287;
  pdf.setDrawColor(36, 84, 156); pdf.setLineWidth(0.3); pdf.line(m, footerY, pw - m, footerY);
  pdf.setFontSize(6.5); pdf.setTextColor(140); pdf.setFont(undefined, 'normal');
  pdf.text('System-generated Quotation from Prismoline ERP', pw / 2, footerY + 4, { align: 'center' });

  pdf.save(`Signage_Quote_${form.project_name || 'draft'}.pdf`);
};
